import {useContext, useMemo} from 'preact/hooks';

import {ShadowRootContext} from 'utilities/dom';
import {useThemeOverride} from 'utilities/mount';

import {
  getModuleBackgroundColor,
  getModuleFontColor,
  getNearestParentElementBackground,
  pickThemeColor,
  shouldOverrideFont,
} from './helpers';
import {Theme} from './types';

export function useTheme(): {
  theme: Theme;
  fontOverride: boolean;
} {
  const shadowRoot = useContext(ShadowRootContext);
  const themeOverride = useThemeOverride();
  const nearestParentElementBackground = useMemo(
    () => getNearestParentElementBackground(shadowRoot?.host as HTMLElement),
    [shadowRoot?.host],
  );

  const theme = useMemo(() => {
    if (themeOverride) {
      const theme = themeOverride.toUpperCase();
      return theme === Theme.Dark ? Theme.Dark : Theme.Light;
    }

    return pickThemeColor(nearestParentElementBackground);
  }, [nearestParentElementBackground, themeOverride]);

  const fontOverride = useMemo(() => {
    const moduleBackgroundColor = getModuleBackgroundColor(
      nearestParentElementBackground,
      theme,
    );

    const moduleFontColor = getModuleFontColor(
      getComputedStyle(shadowRoot?.host as HTMLElement).color,
      nearestParentElementBackground,
    );

    return shouldOverrideFont(moduleFontColor, moduleBackgroundColor, theme);
  }, [nearestParentElementBackground, shadowRoot?.host, theme]);

  return {
    theme,
    fontOverride,
  };
}
